<template>
  <div 
    class="game-page-layout"
    :style="{ 
      backgroundImage: background.mainBg ? `url(${background.mainBg})` : '',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat'
    }"
  >
    <!-- Banner Section -->
    <section class="game-banner" v-if="showBanner">
      <BaseBanner 
        v-bind="bannerProps" 
        @onLoaded="handleBannerLoaded"
      />
    </section>
    
    <!-- Main Content Section -->
    <main class="game-content" :class="contentClasses">
      <!-- Search Section -->
      <section class="search-section" v-if="showSearch">
        <slot name="search" :search-handler="searchHandler">
          <Search 
            v-if="defaultSearchComponent"
            @inputText="handleSearch"
            :placeholder="searchPlaceholder"
            :disabled="searchDisabled"
          />
        </slot>
      </section>
      
      <!-- Content Area -->
      <section class="content-area">
        <slot
          name="content"
          :loading="!allInitDone"
        />
      </section>
      
      <!-- Actions Section -->
      <section class="actions-section" v-if="$slots.actions">
        <slot name="actions" />
      </section>
    </main>
    
    <!-- Floating Elements -->
    <div class="floating-elements">
      <!-- Tool/Utility Buttons -->
      <slot name="floating-tools" />
    </div>
    
    <!-- Notifications -->
    <GameNotification v-if="showNotifications" />
    
    <!-- Modals/Popups -->
    <slot name="modals" />
  </div>
</template>

<script>
import BaseBanner from '@/components/game/shared/baseBanner.vue';
import Search from '@/components/game/shared/search.vue';
import GameNotification from '@/components/game/shared/notification.vue';

/**
 * Generic game page layout component following SOLID principles
 * Single Responsibility: Provides consistent layout structure for game pages
 * Open/Closed Principle: Extensible through slots without modification
 */
export default {
  name: 'GamePageLayout',
  
  components: {
    BaseBanner,
    Search,
    GameNotification
  },
  
  props: {
    // Banner configuration
    bannerProps: {
      type: Object,
      default: () => ({})
    },
    showBanner: {
      type: Boolean,
      default: true
    },
    
    // Background configuration
    background: {
      type: Object,
      default: () => ({ mainBg: '' })
    },
    
    // Search configuration
    showSearch: {
      type: Boolean,
      default: true
    },
    defaultSearchComponent: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      default: ''
    },
    searchDisabled: {
      type: Boolean,
      default: false
    },
    searchHandler: {
      type: Function,
      default: () => {}
    },
    
    // Layout state
    allInitDone: {
      type: Boolean,
      default: false
    },

    // UI configuration
    showNotifications: {
      type: Boolean,
      default: true
    },
    contentClasses: {
      type: [String, Array, Object],
      default: ''
    }
  },
  
  emits: [
    'banner-loaded',
    'search'
  ],
  
  methods: {
    /**
     * Handle banner loaded event
     */
    handleBannerLoaded() {
      this.$emit('banner-loaded');
    },
    
    /**
     * Handle search input
     * @param {string} queryText - Search query
     */
    handleSearch(queryText) {
      if (this.searchHandler && typeof this.searchHandler === 'function') {
        this.searchHandler(queryText);
      }
      this.$emit('search', queryText);
    }
  }
};
</script>

<style lang="scss" scoped>
.game-page-layout {
  min-height: 100vh;
  width: 100%;
  position: relative;
  
  // Ensure proper layering
  .game-banner {
    position: relative;
    z-index: 2;
  }
  
  .game-content {
    position: relative;
    z-index: 1;
    padding: 1rem;
    
    .search-section {
      margin-bottom: 1rem;
    }
    
    .content-area {
      flex: 1;
      min-height: 0; // Allow flex children to shrink
    }
    
    .actions-section {
      margin-top: 1rem;
    }
  }
  
  .floating-elements {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1000;

    // Allow interactions with floating tools
    > * {
      pointer-events: auto;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .game-page-layout {
    .game-content {
      padding: 0.5rem;
    }
  }
}

// Loading state
.game-content {
  &.loading {
    .content-area {
      opacity: 0.7;
      pointer-events: none;
    }
  }
}
</style>